import { useQuery } from '@tanstack/react-query'
import { useTenantAtual } from '../../hooks/useTenant'
import { useAuthStore } from '../../stores/authStore'
import styles from '../_shared/CrudPage.module.css'

interface ReadinessResponse {
  status: 'ready' | 'not_ready'
  checks: Record<
    string,
    { status: 'ok' | 'error'; latency_ms?: number; detail?: string }
  >
}

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1'

// /health/ e /ready/ ficam fora do prefixo /api/v1 — extraímos a raiz.
function rootBase(): string {
  if (API_BASE_URL.startsWith('/')) return ''
  try {
    const u = new URL(API_BASE_URL)
    return `${u.protocol}//${u.host}`
  } catch {
    return ''
  }
}

function useHealth() {
  return useQuery({
    queryKey: ['sistema', 'health'],
    queryFn: async () => {
      const r = await fetch(`${rootBase()}/health/`)
      return { ok: r.ok, status: r.status }
    },
    refetchInterval: 30_000,
  })
}

function useReadiness() {
  return useQuery({
    queryKey: ['sistema', 'readiness'],
    queryFn: async (): Promise<ReadinessResponse> => {
      const r = await fetch(`${rootBase()}/ready/`)
      return r.json()
    },
    refetchInterval: 30_000,
  })
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.2rem 0.6rem',
        borderRadius: 999,
        background: ok ? '#E8F5E9' : '#FFEBEE',
        color: ok ? '#2E7D32' : '#C62828',
        fontWeight: 600,
        fontSize: '0.75rem',
      }}
    >
      {ok ? '● ' : '○ '}
      {label}
    </span>
  )
}

export default function SistemaPage() {
  const tenant = useTenantAtual()
  const user = useAuthStore((s) => s.user)
  const health = useHealth()
  const readiness = useReadiness()

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Saúde do sistema</h1>
          <p className={styles.subtitle}>
            Status de runtime, dependências e contexto da sessão. Atualiza a cada 30s.
          </p>
        </div>
      </header>

      <section
        style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: 8,
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Backend</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem' }}>
          <dt style={{ color: '#666' }}>Liveness (/health/):</dt>
          <dd style={{ margin: 0 }}>
            <StatusPill
              ok={!!health.data?.ok}
              label={health.isLoading ? 'verificando...' : health.data?.ok ? 'OK' : 'falha'}
            />
          </dd>
          <dt style={{ color: '#666' }}>Readiness (/ready/):</dt>
          <dd style={{ margin: 0 }}>
            <StatusPill
              ok={readiness.data?.status === 'ready'}
              label={
                readiness.isLoading
                  ? 'verificando...'
                  : readiness.data?.status === 'ready'
                    ? 'pronto'
                    : 'não pronto'
              }
            />
          </dd>
          {readiness.data?.checks &&
            Object.entries(readiness.data.checks).map(([nome, c]) => (
              <div key={nome} style={{ display: 'contents' }}>
                <dt style={{ color: '#666', textTransform: 'capitalize' }}>{nome}:</dt>
                <dd style={{ margin: 0 }}>
                  <StatusPill ok={c.status === 'ok'} label={c.status} />
                  {c.latency_ms != null && (
                    <span style={{ marginLeft: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                      ({c.latency_ms} ms)
                    </span>
                  )}
                  {c.detail && (
                    <p
                      style={{
                        margin: '0.25rem 0 0',
                        color: '#C62828',
                        fontSize: '0.8rem',
                      }}
                    >
                      {c.detail}
                    </p>
                  )}
                </dd>
              </div>
            ))}
        </dl>
      </section>

      <section
        style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: 8,
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Tenant atual</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem' }}>
          <dt style={{ color: '#666' }}>Nome:</dt>
          <dd style={{ margin: 0 }}>{tenant.data?.nome ?? '—'}</dd>
          <dt style={{ color: '#666' }}>CNPJ:</dt>
          <dd style={{ margin: 0 }}>{tenant.data?.cnpj ?? '—'}</dd>
          <dt style={{ color: '#666' }}>CREA PJ:</dt>
          <dd style={{ margin: 0 }}>
            {tenant.data?.registro_crea_pj ?? '—'}
            {tenant.data?.uf_registro && ` / ${tenant.data.uf_registro}`}
          </dd>
        </dl>
      </section>

      <section
        style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Sessão atual</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem' }}>
          <dt style={{ color: '#666' }}>Usuário:</dt>
          <dd style={{ margin: 0 }}>{user?.nomeCompleto ?? '—'}</dd>
          <dt style={{ color: '#666' }}>E-mail:</dt>
          <dd style={{ margin: 0 }}>{user?.email ?? '—'}</dd>
          <dt style={{ color: '#666' }}>Perfil:</dt>
          <dd style={{ margin: 0 }}>{user?.perfil ?? '—'}</dd>
          <dt style={{ color: '#666' }}>API base:</dt>
          <dd style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {API_BASE_URL}
          </dd>
        </dl>
      </section>
    </div>
  )
}
